import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Calendar, Plus, Target, People, ChartLine } from '@factorialco/f0-react/icons/app'
import { IncentivesCompensationPlansDataCollectionDocument } from 'generated/resources/graphql'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'
import { periodLabel } from 'modules/incentives/lib/period'
import { recurrenceLabel } from 'modules/incentives/lib/recurrence'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { CreateCompensationPlanDialog } from 'modules/incentives/components/CreateCompensationPlanDialog'

export const CompensationPlansDataCollection = () => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  const source = useGraphqlDataCollectionSource({
    totalItemSummary: true,
    document: IncentivesCompensationPlansDataCollectionDocument,
    perPage: 20,
    mapData: (data) => data.incentives.compensationPlansConnection.nodes || [],
    mapPagination: (data) => ({
      total: data.incentives.compensationPlansConnection.totalCount ?? 0,
    }),
    mapVariables: ({ search }) => ({
      companyId: company.id,
      search,
    }),
    search: {
      enabled: true,
    },
    primaryActions: () => ({
      label: i18n.t('incentives.list.new'),
      icon: Plus,
      onClick: () => {
        setIsCreateDialogOpen(true)
      },
    }),
    itemUrl: (item) =>
      navigation.compensations.incentives.plans.id.fullPath({ id: String(item.id) }),
  })

  const emptyState = {
    'no-data': {
      emoji: '💰',
      title: i18n.t('incentives.list.empty_state.label'),
      description: i18n.t('incentives.list.empty_state.description'),
      actions: [
        {
          label: i18n.t('incentives.list.empty_state.button'),
          variant: 'outline' as const,
          onClick: () => setIsCreateDialogOpen(true),
        },
      ],
    },
  }

  return (
    <>
      <OneDataCollection
        fullHeight
        source={source}
        emptyStates={emptyState}
        visualizations={[
          {
            type: 'card',
            options: {
              title: (item) => item.name,
              cardProperties: [
                {
                  label: i18n.t('incentives.plan.recurrence'),
                  icon: Calendar,
                  render: ({ recurrence }) => recurrenceLabel(recurrence),
                },
                {
                  label: i18n.t('incentives.list.employees.label'),
                  icon: People,
                  render: ({ assignmentEmployeesCount }) =>
                    i18n.tp('incentives.list.employees.content', {
                      count: assignmentEmployeesCount,
                    }),
                },
                {
                  label: i18n.t('incentives.list.goals.label'),
                  icon: Target,
                  render: ({ currentPeriod }) => {
                    const metricsCount = currentPeriod?.metricsCount || 0

                    if (metricsCount > 0) {
                      return i18n.tp('incentives.list.goals.content', {
                        count: metricsCount,
                      })
                    }
                    return {
                      type: 'alertTag',
                      value: {
                        level: 'warning',
                        label: i18n.t('incentives.list.goals.alert'),
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
      <CreateCompensationPlanDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={(planId) =>
          navigate(navigation.compensations.incentives.plans.id.fullPath({ id: planId }))
        }
      />
    </>
  )
}
